// AttachmentHandler.js
import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Modal } from "react-bootstrap";
import PDFViewer from "../utils/PDFViewer";
import { getIconForContentType } from "../utils/getIconForContentType";
import { fetchAttachment } from "../utils/fetchAttachment";

/**
 * @param {Object[]} attachments - Array of attachment objects:
 *   { title, url, data, contentType, raw }
 * @param {Object} fhirClient - FHIR client instance
 */
const AttachmentHandler = forwardRef(({ attachments = [], fhirClient, showList = true }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [modalBlobUrl, setModalBlobUrl] = useState("");
  const [modalType, setModalType] = useState("generic");
  const [modalTitle, setModalTitle] = useState("");
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorText, setErrorText] = useState("");

  const handleOpen = async (att) => {
    try {
      const fetched = await fetchAttachment(att, fhirClient);
      // If the attachment resolves to an external URL (e.g. PACS/viewer), inform the user
      if (fetched?.type === "ExternalURL") {
        setErrorText("This attachment is an external resource so cannot be opened by third-party image viewers directly. Please open the resource in its originating system or download it instead.");
        setErrorModalOpen(true);
        return;
      }

      // If the fetched result is a DocumentReference wrapper containing an attachment URL,
      // avoid directly opening external URLs (PACS) and show the error modal instead.
      if (fetched?.type === "DocumentReference") {
        const att = fetched.attachment;
        if (att?.url && /^https?:\/\//i.test(att.url)) {
          setErrorText("Cannot open external PACS or third-party image viewers directly. Please open the resource in its originating system or download it instead.");
          setErrorModalOpen(true);
          return;
        }
        // If it contains inline data, we'll fall through to blob handling (fetchAttachment may provide blob)
      }

      if (!fetched?.blob) {
        setErrorText("Failed to open attachment.");
        setErrorModalOpen(true);
        return;
      }

      const url = URL.createObjectURL(fetched.blob);

      setModalBlobUrl(url);
      setModalTitle(att.title);

      // Determine type for modal rendering
      const contentType = att.contentType || fetched.contentType || "";
      if (contentType.includes("pdf")) setModalType("pdf");
      else if (contentType.startsWith("image/")) setModalType("image");
      else if (contentType.startsWith("audio/")) setModalType("audio");
      else if (contentType.startsWith("video/")) setModalType("video");
      else setModalType("generic");

      setShowModal(true);
    } catch (err) {
      console.error("Failed to open attachment", err);
      setErrorText("Failed to open attachment.");
      setErrorModalOpen(true);
    }
  };

  const handleDownload = async (att) => {
    try {
      const fetched = await fetchAttachment(att, fhirClient);
      // If this is a DocumentReference with an external URL, do not follow it — show error modal
      if (fetched?.type === "DocumentReference") {
        const a = fetched.attachment;
        if (a?.url && /^https?:\/\//i.test(a.url)) {
          setErrorText("Cannot download external PACS or third-party images directly. Please use the originating system.");
          setErrorModalOpen(true);
          return;
        }
      }

      if (!fetched?.blob) {
        setErrorText("Failed to download attachment.");
        setErrorModalOpen(true);
        return;
      }

      // Use blob directly - fetchAttachment already returns a properly formed Blob
      const blobUrl = URL.createObjectURL(fetched.blob);
      console.log("Download blob URL:", blobUrl);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = att.title.replace(/\s+/g, "_");
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download attachment", err);
      setErrorText("Failed to download attachment.");
      setErrorModalOpen(true);
    }
  };

  // Expose imperative methods to parent via ref
  useImperativeHandle(ref, () => ({
    open: handleOpen,
    download: handleDownload,
  }));

  if (!attachments || attachments.length === 0) return <div className="text-muted"></div>;

  return (
    <>
      {showList && (
        <div>
          {attachments.map((att, idx) => (
            <div key={idx} className="border rounded p-2 mb-2 d-flex align-items-start">
              <div className="me-2">{getIconForContentType(att.contentType, "Media")}</div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="fw-bold text-truncate me-3" style={{ maxWidth: "70%" }}>
                    {att.title} <span className="text-muted">({att.contentType || "N/A"})</span>
                  </div>
                  <div className="d-flex">
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleOpen(att)}>Open</button>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleDownload(att)}>Download</button>
                  </div>
                </div>
                {att.creation && <div className="text-muted small">Created: {att.creation}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          if (modalBlobUrl) URL.revokeObjectURL(modalBlobUrl);
          setModalBlobUrl("");
        }}
        size="xl"
        fullscreen="md-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {modalType === "pdf" && <PDFViewer blobUrl={modalBlobUrl} />}
          {modalType === "image" && <img src={modalBlobUrl} alt={modalTitle} style={{ maxWidth: "100%", maxHeight: "100%" }} />}
          {modalType === "video" && <video src={modalBlobUrl} controls style={{ width: "100%", height: "100%" }} />}
          {modalType === "audio" && <audio src={modalBlobUrl} controls style={{ width: "100%" }} />}
          {modalType === "generic" && <iframe src={modalBlobUrl} title={modalTitle} style={{ width: "100%", height: "100%", border: "none" }} />}
        </Modal.Body>
      </Modal>
      {/* Error modal */}
      <Modal show={errorModalOpen} onHide={() => setErrorModalOpen(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#ffcccc" }}>
          <Modal.Title>Attachment Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-break">{errorText}</p>
        </Modal.Body>
      </Modal>
    </>
  );
});

export default AttachmentHandler;
